import { useState, useCallback } from 'preact/hooks'
import { History } from 'lucide-preact'
import { ConfirmationModal } from '@/components/modals/ConfirmationModal'
import { assetApi } from '@/api/asset'
import { useToast } from '@/hooks/useToast'

interface RevertDraftButtonProps {
    clientCompanyId: string
    workspaceId: string
    campaignId: string
    assetId: string
    /** The version to restore — the latest committed ("last good") version. */
    versionNumber: number
    /** Disable while a version save or Webster rebuild is in flight. */
    disabled?: boolean
    /** Fires after a successful revert so the parent can refetch + reseed the editor
     *  off the restored DraftDesign. */
    onReverted?: () => Promise<void> | void
}

/**
 * "Revert to last version" action surfaced in the Webster email builder's top bar.
 * Discards the current draft and reloads the latest committed version into it via
 * loadVersionIntoDraft — the recovery escape hatch for a draft that drifted or broke
 * (see ORB-301). Distinct from the Email Copy editor's "Reset to template", which
 * re-clones the bound template's design rather than restoring a saved version.
 * Behind a confirmation modal so accidental clicks don't blow away draft edits.
 */
export function RevertDraftButton({
    clientCompanyId,
    workspaceId,
    campaignId,
    assetId,
    versionNumber,
    disabled,
    onReverted,
}: RevertDraftButtonProps) {
    const [showConfirm, setShowConfirm] = useState(false)
    const [reverting, setReverting] = useState(false)
    const { showSuccess, showError } = useToast()

    const handleConfirm = useCallback(async () => {
        setReverting(true)
        const response = await assetApi.loadVersionIntoDraft(clientCompanyId, workspaceId, campaignId, assetId, versionNumber)
        setReverting(false)
        if (response.error) {
            showError('Revert failed', response.error || 'Could not revert the draft to the last saved version.')
            return
        }
        setShowConfirm(false)
        showSuccess('Draft reverted', `Restored version ${versionNumber}.`)
        await onReverted?.()
    }, [clientCompanyId, workspaceId, campaignId, assetId, versionNumber, onReverted, showSuccess, showError])

    return (
        <>
            <button
                type="button"
                onClick={() => setShowConfirm(true)}
                disabled={disabled || reverting}
                title={`Discard your current draft changes and restore the last saved version (v${versionNumber})`}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <History className="w-4 h-4" />
                Revert to last version
            </button>

            <ConfirmationModal
                isOpen={showConfirm}
                title="Revert to last saved version?"
                message={`Your current draft changes will be discarded and the design restored to version ${versionNumber}. This can't be undone.`}
                type="warning"
                confirmText="Revert"
                cancelText="Cancel"
                loading={reverting}
                onConfirm={handleConfirm}
                onCancel={() => { if (!reverting) setShowConfirm(false) }}
            />
        </>
    )
}
